import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import LoadingSpinner from "Components/Loader/LoadingSpinner";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  accommodations,
  educationLevels,
  nationalities,
} from "../../../consts";
import {
  fetchStudentProfile,
  updateStudentProfile,
} from "../../../redux/Actions/studentActions";
import MessageCard from "../../MessageCard/MessageCard";
import Meta from "../../Meta/Meta";
import ChangePasswordCard from "./ChangePasswordCard";
import { StudentDashboardLayout } from "./StudentDashboard";

const PHONE_COUNTRIES = [
  { code: "+20", flag: "🇪🇬", country: "Egypt" },
  { code: "+49", flag: "🇩🇪", country: "Germany" },
  { code: "+43", flag: "🇦🇹", country: "Austria" },
  { code: "+41", flag: "🇨🇭", country: "Switzerland" },
  { code: "+971", flag: "🇦🇪", country: "United Arab Emirates" },
  { code: "+966", flag: "🇸🇦", country: "Saudi Arabia" },
  { code: "+974", flag: "🇶🇦", country: "Qatar" },
  { code: "+965", flag: "🇰🇼", country: "Kuwait" },
  { code: "+973", flag: "🇧🇭", country: "Bahrain" },
  { code: "+968", flag: "🇴🇲", country: "Oman" },
  { code: "+962", flag: "🇯🇴", country: "Jordan" },
  { code: "+961", flag: "🇱🇧", country: "Lebanon" },
  { code: "+963", flag: "🇸🇾", country: "Syria" },
  { code: "+964", flag: "🇮🇶", country: "Iraq" },
  { code: "+212", flag: "🇲🇦", country: "Morocco" },
  { code: "+213", flag: "🇩🇿", country: "Algeria" },
  { code: "+216", flag: "🇹🇳", country: "Tunisia" },
  { code: "+90", flag: "🇹🇷", country: "Turkey" },
  { code: "+44", flag: "🇬🇧", country: "United Kingdom" },
  { code: "+1", flag: "🇺🇸", country: "United States / Canada" },
];

const splitPhoneNumber = (phoneNumber) => {
  const value = String(phoneNumber || "").trim().replace(/[\s()-]/g, "");
  const match = [...PHONE_COUNTRIES]
    .sort((a, b) => b.code.length - a.code.length)
    .find(({ code }) => value.startsWith(code));

  return match
    ? { dialCode: match.code, localNumber: value.slice(match.code.length) }
    : { dialCode: "+20", localNumber: value };
};

function StudentProfile() {
  const { t } = useTranslation(["ui"]); // use ui namespace

  const dispatch = useDispatch();
  const profile = useSelector((state) => state.student.profile);
  const loading = useSelector((state) => state.student.loading);
  const updateSuccess = useSelector((state) => state.student.updateSuccess);
  const updateError = useSelector((state) => state.student.updateError);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: "",
    job: "",
    nationality: "",
    accommodation: "",
    phoneNumber: "",
  });

  const [showPasswordCard, setShowPasswordCard] = useState(false);
  const [dialCode, setDialCode] = useState("+20");
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  const userId = useSelector((state) => state.auth.user.userId);
  const roleName = useSelector((state) => state.auth.user.roleName);

  useEffect(() => {
    if (userId && roleName) {
      dispatch(fetchStudentProfile(userId, roleName));
    }
    return () => {
      dispatch({ type: "CLEAR_UPDATE_STATE" });
    };
  }, [dispatch, userId, roleName]);

  useEffect(() => {
    if (profile) {
      const formattedDate = profile.dateOfBirth
        ? new Date(profile.dateOfBirth).toISOString().split("T")[0]
        : "";
      const parsedPhone = splitPhoneNumber(profile.phoneNumber);
      setDialCode(parsedPhone.dialCode);
      setFormData({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        dateOfBirth: formattedDate,
        job: profile.job || "",
        nationality: profile.nationality || "",
        accommodation: profile.accommodation || "",
        phoneNumber: parsedPhone.localNumber,
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = (e) => {
    e.preventDefault();
    try {
      const localNumber = String(formData.phoneNumber || "")
        .trim()
        .replace(/[\s()-]/g, "")
        .replace(/^0+/, "");
      dispatch(updateStudentProfile({
        ...formData,
        phoneNumber: localNumber ? `${dialCode}${localNumber}` : "",
      }));
      if (updateError) throw new Error(updateError);
      if (updateSuccess) {
        setMessage(t("Profile updated successfully."));
        setMessageType("success");
      }
    } catch (error) {
      setMessage(t("Failed to update profile. Please try again."));
      setMessageType("error");
    }
  };

  const handleCloseMessage = () => setMessage(null);
  const handleShowPasswordCard = () => setShowPasswordCard(true);
  const handleClosePasswordCard = () => setShowPasswordCard(false);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Meta
        title="My Profile | ALGermanist"
        description="Manage your personal information, contact details, and account security on ALGermanist."
        keywords="profile, account settings, update profile, ALGermanist"
      />
      <StudentDashboardLayout>
        <Box
          sx={{
            maxWidth: 700,
            mx: "auto",
            mt: 12,
            mb: { xs: 16, sm: 12 },
            px: 4,
            py: 5,
            bgcolor: "white",
            borderRadius: 3,
            boxShadow: { xs: 0, sm: 3 },
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: "bold", mb: 3 }}
          >
            <span role="img" aria-label="profile">
              ??
            </span>{" "}
            {t("signup.updateProfile")}
          </Typography>

          <form onSubmit={handleUpdateProfile}>
            <Grid container spacing={2}>
              {[
                { name: "firstName", label: t("signup.firstName") },
                { name: "lastName", label: t("signup.lastName") },
                { name: "email", label: t("signup.email"), disabled: true },
                {
                  name: "dateOfBirth",
                  label: t("signup.dateOfBirth"),
                  type: "date",
                  InputLabelProps: { shrink: true },
                },
              ].map(({ name, label, ...props }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <TextField
                    label={label}
                    name={name}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData[name]}
                    onChange={handleChange}
                    {...props}
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    direction: "ltr",
                  }}
                >
                  <FormControl margin="normal" sx={{ flex: "0 0 145px" }}>
                    <InputLabel id="phone-country-code-label">
                      {t("signup.phoneCountryCode")}
                    </InputLabel>
                    <Select
                      labelId="phone-country-code-label"
                      label={t("signup.phoneCountryCode")}
                      value={dialCode}
                      onChange={(event) => setDialCode(event.target.value)}
                      renderValue={(value) => {
                        const selected = PHONE_COUNTRIES.find(({ code }) => code === value);
                        return selected ? `${selected.flag} ${selected.code}` : value;
                      }}
                    >
                      {PHONE_COUNTRIES.map(({ code, flag, country }) => (
                        <MenuItem key={`${country}-${code}`} value={code}>
                          <Box component="span" sx={{ mr: 1, fontSize: 20 }}>{flag}</Box>
                          <Box component="span" sx={{ mr: 1 }}>{code}</Box>
                          <Box component="span" sx={{ color: "text.secondary" }}>{country}</Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label={t("signup.phoneNumber")}
                    name="phoneNumber"
                    type="tel"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    inputProps={{ inputMode: "tel", dir: "ltr" }}
                    sx={{ minWidth: 0 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="education-label">
                    {t("signup.educationLevel")}
                  </InputLabel>
                  <Select
                    labelId="education-label"
                    label={t("signup.educationLevel")}
                    name="job"
                    value={formData.job}
                    onChange={handleChange}
                  >
                    {educationLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="nationality-label">
                    {t("signup.nationality")}
                  </InputLabel>
                  <Select
                    labelId="nationality-label"
                    label={t("signup.nationality")}
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                  >
                    {nationalities.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="accommodation-label">
                    {t("signup.accommodation")}
                  </InputLabel>
                  <Select
                    labelId="accommodation-label"
                    name="accommodation"
                    value={formData.accommodation}
                    label={t("signup.accommodation")}
                    onChange={handleChange}
                  >
                    {accommodations.map((a) => (
                      <MenuItem key={a.value} value={a.value}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ py: 1.5, fontWeight: "bold" }}
              >
                {t("signup.updateProfile")}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  py: 1.5,
                  mx: { sm: 3 },
                  fontWeight: "bold",
                  borderRadius: 2,
                }}
                onClick={handleShowPasswordCard}
              >
                {t("signup.changePassword")}
              </Button>
            </Box>
          </form>
        </Box>

        {message && (
          <MessageCard
            message={message}
            type={messageType}
            onClose={handleCloseMessage}
          />
        )}

        {showPasswordCard && (
          <ChangePasswordCard onClose={handleClosePasswordCard} />
        )}
      </StudentDashboardLayout>
    </>
  );
}

export default StudentProfile;
